import { createAsyncThunk } from '@reduxjs/toolkit';
import { PaginationType } from '../../types/utils/pagination.type';
import { DIDsListItemType } from '../../types/did/did.type';
import { ThemeType } from '../../types/theme.type';
import { RouteParamsType } from '../../types/utils/router-params.type';
import { TcServiceTypeType } from '../../types/tc-service/tc-service-type/tc-service-type.type';
import axios from 'axios';
import { additionalQueryParams, groupBy, readTree } from '../../utils/utilities';
import { ROLECONSTANTS } from '../utils/utils.action';
import {
  setCachedCountriesByOrganizationID,
  setCachedGroupedCountriesByOrganizationID,
  setFullCountriesList,
  setFullExternalProviderList,
  setFullExternalSystemList,
  setFullGroupedCountriesList,
} from './shared.slice';
import { rejectWithApiError } from '../../utils/api';
import { RootReducerState } from '../../redux/Reducers';
import { DialerTypeType } from '../../types/dialer/dialer-type/dialer-type.type';
import { LocationListItemType } from '../../types/location/location.type';

export const getCustomerDashboardDIDsList = createAsyncThunk<
  PaginationType<DIDsListItemType>,
  RouteParamsType & { controller?: AbortController },
  { state: RootReducerState }
>('shared/getCustomerDashboardDIDsList/no-loader', async ({ controller, ...params }, thunkAPI) => {
  try {
    const additionalParams = additionalQueryParams(
      thunkAPI.getState().sharedReducer.selectedCompany,
      thunkAPI.getState().sharedReducer.selectedLocation,
      thunkAPI.getState().sharedReducer.includeSuborgs,
      thunkAPI.getState().sharedReducer.selectedCountry
    );
    const in_service = thunkAPI.getState().sharedReducer.customerDashboardInService?.id;
    const mergedParams = { ...params, ...additionalParams };
    if (in_service === 'full_service') {
      mergedParams.full_service = 'true';
    } else {
      mergedParams.in_service = in_service;
    }
    const queryString = Object.keys(mergedParams)
      .map(key => key + '=' + mergedParams[key])
      .join('&');

    const response = await axios.get<PaginationType<DIDsListItemType>>(
      `${process.env.REACT_APP_API_URL}/dids${queryString ? `?${queryString}` : ''}`,
      controller ? { signal: controller.signal } : undefined
    );
    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during fetching DIDs!' });
  }
});

export const getCustomerDashboardLocationsList = createAsyncThunk<
  PaginationType<LocationListItemType>,
  RouteParamsType & { controller?: AbortController },
  { state: RootReducerState }
>('shared/getCustomerDashboardLocationsList/no-loader', async ({ controller, ...params }, thunkAPI) => {
  try {
    const additionalParams = additionalQueryParams(
      thunkAPI.getState().sharedReducer.selectedCompany,
      thunkAPI.getState().sharedReducer.selectedLocation,
      thunkAPI.getState().sharedReducer.includeSuborgs,
      thunkAPI.getState().sharedReducer.selectedCountry
    );
    const in_service = thunkAPI.getState().sharedReducer.customerDashboardInService?.id;
    const mergedParams = { ...params, ...additionalParams };
    if (in_service === 'full_service') {
      mergedParams.full_service = 'true';
    } else {
      mergedParams.in_service = in_service;
    }
    const queryString = Object.keys(mergedParams)
      .map(key => key + '=' + mergedParams[key])
      .join('&');

    const response = await axios.get<PaginationType<LocationListItemType>>(
      `${process.env.REACT_APP_API_URL}/location${queryString ? `?${queryString}` : ''}`,
      controller ? { signal: controller.signal } : undefined
    );
    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during fetching Locations!' });
  }
});

export const getDomainTheme = createAsyncThunk<ThemeType, void, { state: RootReducerState; rejectValue: string }>(
  'shared/getDomainTheme',
  async (_, thunkAPI) => {
    try {
      const response = await axios.get<ThemeType>(`${process.env.REACT_APP_API_URL}/organizations/brand`);
      return response.data;
    } catch (error) {
      return rejectWithApiError({
        thunkAPI,
        error,
        fallback: 'We had trouble loading your domain settings. Please try again.',
      });
    }
  }
);

export const getOrganizationTheme = createAsyncThunk<
  ThemeType,
  number,
  { state: RootReducerState; rejectValue: string }
>('shared/getOrganizationTheme/no-loader', async (orgId, thunkAPI) => {
  try {
    // NOTE: This entire endpoint should be moved from TelcoBill to main API - it is perfect for our use case and isn't coupled to TelcoBill in any way.
    //       Keeping it as-is for now to avoid breakage in other portals, but we should move it in the future.
    const response = await axios.get<ThemeType>(
      `${process.env.REACT_APP_API_URL}/telcobill/organizations/theme-settings/${orgId}`
    );
    return response.data;
  } catch (error) {
    return rejectWithApiError({
      thunkAPI,
      error,
      fallback: 'We had trouble loading your organization settings. Please try again.',
    });
  }
});

export const getJotFormURL = createAsyncThunk<string, void, { state: RootReducerState }>(
  'shared/getJotFormURL',
  async (_, thunkAPI) => {
    try {
      const user = thunkAPI.getState().authReducer.user;
      const selectedCompany = thunkAPI.getState().sharedReducer.selectedCompany;

      const response = await axios.get<string>(
        `${process.env.REACT_APP_API_URL}/organizations/${
          selectedCompany?.id === 'all' ? user?.organization?.id : selectedCompany?.id
        }/jot-form`
      );
      return response.data;
    } catch (error) {
      return rejectWithApiError({ thunkAPI, error, fallback: 'Error during getting jot form URL!' });
    }
  }
);

export const getFullOrganizationList = createAsyncThunk<
  any,
  {
    orgId: number | null | string;
    parentFor?: any;
    strictCondition?: boolean;
  },
  { state: RootReducerState }
>('shared/getFullOrganizationList', async ({ strictCondition = false, orgId = null, parentFor = null }, thunkAPI) => {
  try {
    const user = thunkAPI.getState().authReducer.user;
    const isSuperAdmin = user?.role === ROLECONSTANTS.superadmin;
    const params = { limit: 1000, page: 1, order: 'organization.id' };
    if (isSuperAdmin && !strictCondition) {
      params['org_id'] = user?.organization?.id;
    } else {
      if (orgId === 'all') {
        params['org_id'] = user?.organization?.id;
      } else {
        if (orgId) params['org_id'] = orgId;
      }
    }
    if (parentFor !== null) {
      params['parent_for'] = parentFor;
    }

    const queryString = Object.keys(params || {})
      .map(key => key + '=' + params[key])
      .join('&');

    const url = `${process.env.REACT_APP_API_URL}/organizations/tree${queryString ? `?${queryString}` : ''}`;

    const response = await axios.get<any>(url);

    let obj = [];
    if (response.data?.id) {
      obj = readTree(response.data, '', null);
    }

    return obj;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during getting full organizations list!' });
  }
});

export const getFullLocationList = createAsyncThunk<any, any, { state: RootReducerState }>(
  'shared/getFullLocationList',
  async (data = {}, thunkAPI) => {
    try {
      const params = { limit: 1000, page: 1, order: 'name' };
      Object.keys(data).forEach(key => {
        params[key] = data[key];
      });
      const queryString = Object.keys(params || {})
        .map(key => key + '=' + params[key])
        .join('&');

      const response = await axios.get<any>(
        `${process.env.REACT_APP_API_URL}/location${queryString ? `?${queryString}` : ''}`
      );
      return response.data?.items || [];
    } catch (error) {
      return rejectWithApiError({ thunkAPI, error, fallback: 'Error during getting full locations list!' });
    }
  }
);

export const getFullRouterList = createAsyncThunk<any, any, { state: RootReducerState }>(
  'shared/getFullRouterList',
  async (data = {}, thunkAPI) => {
    try {
      const params = { limit: 1000, page: 1, order: 'id' };
      Object.keys(data).forEach(key => {
        params[key] = data[key];
      });
      const queryString = Object.keys(params || {})
        .map(key => key + '=' + params[key])
        .join('&');

      const response = await axios.get<any>(
        `${process.env.REACT_APP_API_URL}/routers${queryString ? `?${queryString}` : ''}`
      );
      return response.data?.items || [];
    } catch (error) {
      return rejectWithApiError({ thunkAPI, error, fallback: 'Error during getting full routers list!' });
    }
  }
);

export const getFullServiceList = createAsyncThunk<any, { locationId: number }, { state: RootReducerState }>(
  'shared/getFullServiceList',
  async ({ locationId }, thunkAPI) => {
    try {
      const response = await axios.get<any>(`${process.env.REACT_APP_API_URL}/location/${locationId}/services`);
      return response.data?.services || [];
    } catch (error) {
      return rejectWithApiError({ thunkAPI, error, fallback: 'Error during getting full services list!' });
    }
  }
);

export const getFullSimsList = createAsyncThunk<any, any, { state: RootReducerState }>(
  'shared/getFullSimsList',
  async (data = {}, thunkAPI) => {
    try {
      const params = { limit: 1000, page: 1, order: 'id' };
      Object.keys(data).forEach(key => {
        params[key] = data[key];
      });
      const queryString = Object.keys(params || {})
        .map(key => key + '=' + params[key])
        .join('&');

      const response = await axios.get<any>(
        `${process.env.REACT_APP_API_URL}/tc-sim${queryString ? `?${queryString}` : ''}`
      );
      return response.data?.items || [];
    } catch (error) {
      return rejectWithApiError({ thunkAPI, error, fallback: 'Error during getting full SIMs list!' });
    }
  }
);

export const getFullDataPlanTypeList = createAsyncThunk<any, any, { state: RootReducerState }>(
  'shared/getFullDataPlanTypeList',
  async (data = {}, thunkAPI) => {
    try {
      const params = { limit: 1000, page: 1, order: 'id' };
      Object.keys(data).forEach(key => {
        params[key] = data[key];
      });
      const queryString = Object.keys(params || {})
        .map(key => key + '=' + params[key])
        .join('&');

      const response = await axios.get<any>(
        `${process.env.REACT_APP_API_URL}/data-plans/types${queryString ? `?${queryString}` : ''}`
      );
      return response.data?.items || [];
    } catch (error) {
      return rejectWithApiError({ thunkAPI, error, fallback: 'Error during getting full data plan types list!' });
    }
  }
);

export const getFullServiceTypeList = createAsyncThunk(
  'shared/getFullServiceTypeList',
  async (data: { page?: number; limit?: number; order?: string; category?: string; query?: string } = {}, thunkAPI) => {
    try {
      const params = { limit: 1000, page: 1, order: 'id' };
      Object.keys(data).forEach(key => {
        params[key] = data[key];
      });
      const queryString = Object.keys(params || {})
        .map(key => key + '=' + params[key])
        .join('&');

      const response = await axios.get<PaginationType<TcServiceTypeType>>(
        `${process.env.REACT_APP_API_URL}/tc-service-type${queryString ? `?${queryString}` : ''}`
      );
      return response.data?.items || [];
    } catch (error) {
      return rejectWithApiError({ thunkAPI, error, fallback: 'Error during getting full service types list!' });
    }
  }
);

export const getFullEquipmentGroupList = createAsyncThunk<any, any, { state: RootReducerState }>(
  'shared/getFullEquipmentGroupList',
  async (data = {}, thunkAPI) => {
    try {
      const params = { limit: 1000, page: 1, order: 'description' };
      Object.keys(data).forEach(key => {
        params[key] = data[key];
      });
      const queryString = Object.keys(params || {})
        .map(key => key + '=' + params[key])
        .join('&');

      const response = await axios.get<any>(
        `${process.env.REACT_APP_API_URL}/equipment-group${queryString ? `?${queryString}` : ''}`
      );
      return response.data?.items || [];
    } catch (error) {
      return rejectWithApiError({ thunkAPI, error, fallback: 'Error during getting full equipment groups list!' });
    }
  }
);

export const getFullPBXList = createAsyncThunk<any, any, { state: RootReducerState }>(
  'shared/getFullPBXList',
  async (data = {}, thunkAPI) => {
    try {
      const params = { limit: 1000, page: 1, order: 'pbx.description' };
      Object.keys(data).forEach(key => {
        params[key] = data[key];
      });

      const response = await axios.get<any>(`${process.env.REACT_APP_API_URL}/pbxs`, { params });
      return response.data?.items || [];
    } catch (error) {
      return rejectWithApiError({ thunkAPI, error, fallback: 'Error during getting full PBXs list!' });
    }
  }
);

export const getFullATATypeList = createAsyncThunk<any, any, { state: RootReducerState }>(
  'shared/getFullATATypeList',
  async (data = {}, thunkAPI) => {
    try {
      const params = { limit: 1000, page: 1, order: 'id' };
      Object.keys(data).forEach(key => {
        params[key] = data[key];
      });
      const queryString = Object.keys(params || {})
        .map(key => key + '=' + params[key])
        .join('&');

      const response = await axios.get<any>(
        `${process.env.REACT_APP_API_URL}/atas/types${queryString ? `?${queryString}` : ''}`
      );
      return response.data?.items || [];
    } catch (error) {
      return rejectWithApiError({ thunkAPI, error, fallback: 'Error during getting full ATA types list!' });
    }
  }
);

export const getFullDialerTypesList = createAsyncThunk<DialerTypeType[], any, { state: RootReducerState }>(
  'shared/getFullDialerTypesList',
  async (data = {}, thunkAPI) => {
    try {
      const params = { limit: 1000, page: 1, order: 'id' };
      Object.keys(data).forEach(key => {
        params[key] = data[key];
      });
      const queryString = Object.keys(params || {})
        .map(key => key + '=' + params[key])
        .join('&');

      const response = await axios.get<PaginationType<DialerTypeType>>(
        `${process.env.REACT_APP_API_URL}/dialer-type${queryString ? `?${queryString}` : ''}`
      );
      return response.data?.items || [];
    } catch (error) {
      return rejectWithApiError({ thunkAPI, error, fallback: 'Error during getting full dialer types list!' });
    }
  }
);

export const getFullATAOrgTemplateList = createAsyncThunk<any, any, { state: RootReducerState }>(
  'shared/getFullATAOrgTemplateList',
  async (data = {}, thunkAPI) => {
    try {
      const params = { limit: 1000, page: 1, order: 'id' };
      Object.keys(data).forEach(key => {
        params[key] = data[key];
      });
      const queryString = Object.keys(params || {})
        .map(key => key + '=' + params[key])
        .join('&');

      const response = await axios.get<any>(
        `${process.env.REACT_APP_API_URL}/ata-org-templates${queryString ? `?${queryString}` : ''}`
      );
      return response.data?.items || [];
    } catch (error) {
      return rejectWithApiError({ thunkAPI, error, fallback: 'Error during getting full ATA org template list!' });
    }
  }
);

export const getFullATABaseTemplateList = createAsyncThunk<any, any, { state: RootReducerState }>(
  'shared/getFullATABaseTemplateList',
  async (data = {}, thunkAPI) => {
    try {
      const params = { limit: 1000, page: 1, order: 'id' };
      Object.keys(data).forEach(key => {
        params[key] = data[key];
      });
      const queryString = Object.keys(params || {})
        .map(key => key + '=' + params[key])
        .join('&');

      const response = await axios.get<any>(
        `${process.env.REACT_APP_API_URL}/ata-base-templates${queryString ? `?${queryString}` : ''}`
      );
      return response.data?.items || [];
    } catch (error) {
      return rejectWithApiError({ thunkAPI, error, fallback: 'Error during getting full ATA base template list!' });
    }
  }
);

export const getFullBucketsList = createAsyncThunk<any, any, { state: RootReducerState }>(
  'shared/getFullBucketsList',
  async (data = {}, thunkAPI) => {
    try {
      const params = { limit: 1000, page: 1, order: '-bucket.id' };
      Object.keys(data).forEach(key => {
        params[key] = data[key];
      });
      const queryString = Object.keys(params || {})
        .map(key => key + '=' + params[key])
        .join('&');

      const response = await axios.get<any>(
        `${process.env.REACT_APP_API_URL}/secure-alarm/bucket${queryString ? '?' + queryString : ''}`
      );
      return response.data?.items || [];
    } catch (error) {
      return rejectWithApiError({ thunkAPI, error, fallback: 'Error during getting full buckets list!' });
    }
  }
);

export const getFullATAACSProfileList = createAsyncThunk<any, any, { state: RootReducerState }>(
  'shared/getFullATAACSProfileList',
  async (data = {}, thunkAPI) => {
    try {
      const params = { limit: 1000, page: 1, order: 'id' };
      Object.keys(data).forEach(key => {
        params[key] = data[key];
      });
      const queryString = Object.keys(params || {})
        .map(key => key + '=' + params[key])
        .join('&');

      const response = await axios.get<any>(
        `${process.env.REACT_APP_API_URL}/acs/profiles${queryString ? '?' + queryString : ''}`
      );
      return response.data || [];
    } catch (error) {
      return rejectWithApiError({ thunkAPI, error, fallback: 'Error during getting full ATA ACS profiles list!' });
    }
  }
);

export const getFullTCBlockList = createAsyncThunk<any, any, { state: RootReducerState }>(
  'shared/getFullTCBlockList',
  async (data = {}, thunkAPI) => {
    try {
      const params = { limit: 1000, page: 1, order: 'id' };
      Object.keys(data).forEach(key => {
        params[key] = data[key];
      });
      const queryString = Object.keys(params || {})
        .map(key => key + '=' + params[key])
        .join('&');

      const response = await axios.get<any>(
        `${process.env.REACT_APP_API_URL}/tc-block${queryString ? '?' + queryString : ''}`
      );
      return response.data?.items || [];
    } catch (error) {
      return rejectWithApiError({ thunkAPI, error, fallback: 'Error during getting full TC Blocks list!' });
    }
  }
);

export const getFullLineTypesList = createAsyncThunk<any, any, { state: RootReducerState }>(
  'shared/getFullLineTypesList',
  async (data = {}, thunkAPI) => {
    try {
      const params = { limit: 1000, page: 1, order: 'id' };
      Object.keys(data).forEach(key => {
        params[key] = data[key];
      });
      const queryString = Object.keys(params || {})
        .map(key => key + '=' + params[key])
        .join('&');

      const response = await axios.get<any>(
        `${process.env.REACT_APP_API_URL}/line-types${queryString ? '?' + queryString : ''}`
      );
      return response.data?.items || [];
    } catch (error) {
      return rejectWithApiError({ thunkAPI, error, fallback: 'Error during getting full line types list!' });
    }
  }
);

export const getFullVerifiedNumbersList = createAsyncThunk<any, any, { state: RootReducerState }>(
  'shared/getFullVerifiedNumbersList',
  async (data = {}, thunkAPI) => {
    try {
      const params = { limit: 1000, page: 1, order: 'id' };
      Object.keys(data).forEach(key => {
        params[key] = data[key];
      });
      const queryString = Object.keys(params || {})
        .map(key => key + '=' + params[key])
        .join('&');

      const response = await axios.get<any>(
        `${process.env.REACT_APP_API_URL}/verification/number${queryString ? '?' + queryString : ''}`
      );
      return response.data?.items || [];
    } catch (error) {
      return rejectWithApiError({ thunkAPI, error, fallback: 'Error during getting full numbers list!' });
    }
  }
);

export const getFullVerifiedEmailsList = createAsyncThunk<any, any, { state: RootReducerState }>(
  'shared/getFullVerifiedEmailsList',
  async (data = {}, thunkAPI) => {
    try {
      const params = { limit: 1000, page: 1, order: 'id' };
      Object.keys(data).forEach(key => {
        params[key] = data[key];
      });
      const queryString = Object.keys(params || {})
        .map(key => key + '=' + params[key])
        .join('&');

      const response = await axios.get<any>(
        `${process.env.REACT_APP_API_URL}/verification/email${queryString ? '?' + queryString : ''}`
      );
      return response.data?.items || [];
    } catch (error) {
      return rejectWithApiError({ thunkAPI, error, fallback: 'Error during getting full emails list!' });
    }
  }
);

export const getFullCountriesList = createAsyncThunk<any, any, { state: RootReducerState }>(
  'shared/getFullCountriesList',
  async (data = {}, thunkAPI) => {
    try {
      const mergedParams = { ...data };
      if (!mergedParams.org_id) {
        mergedParams.org_id =
          thunkAPI.getState().sharedReducer.selectedCompany?.id === 'all'
            ? thunkAPI.getState().authReducer.user?.organization?.id
            : thunkAPI.getState().sharedReducer.selectedCompany?.id;
      }

      const queryString = Object.keys(mergedParams || {})
        .map(key => key + '=' + mergedParams[key])
        .join('&');

      const cachedCountriesByOrganizationID = thunkAPI.getState().sharedReducer.cachedCountriesByOrganizationID;
      const cachedGroupedCountriesByOrganizationID =
        thunkAPI.getState().sharedReducer.cachedGroupedCountriesByOrganizationID;
      if (Object.keys(cachedCountriesByOrganizationID || {}).find(key => +key === +mergedParams.org_id)) {
        thunkAPI.dispatch(
          setFullGroupedCountriesList(
            cachedGroupedCountriesByOrganizationID ? cachedGroupedCountriesByOrganizationID[mergedParams.org_id] : []
          )
        );
        thunkAPI.dispatch(
          setFullCountriesList(
            cachedCountriesByOrganizationID ? cachedCountriesByOrganizationID[mergedParams.org_id] : []
          )
        );
        return;
      }

      const response = await axios.get<any>(
        `${process.env.REACT_APP_API_URL}/countries/short${queryString ? `?${queryString}` : ''}`
      );

      const groupedCountries = groupBy(response.data.items, 'category');
      const fullCountriesList = Object.keys(groupedCountries).map(key => ({
        name: key,
        options: groupedCountries[key],
      }));

      thunkAPI.dispatch(setFullGroupedCountriesList(fullCountriesList || []));
      thunkAPI.dispatch(setFullCountriesList(response.data.items || []));

      thunkAPI.dispatch(setCachedCountriesByOrganizationID({ [mergedParams.org_id]: response.data.items || [] }));
      thunkAPI.dispatch(setCachedGroupedCountriesByOrganizationID({ [mergedParams.org_id]: fullCountriesList || [] }));

      return response.data?.items || [];
    } catch (error) {
      return rejectWithApiError({ thunkAPI, error, fallback: 'Error during getting full countries list!' });
    }
  }
);

export const getRouterProductNameTypesList = createAsyncThunk<any, any, { state: RootReducerState }>(
  'shared/getRouterProductNameTypesList',
  async (data = {}, thunkAPI) => {
    try {
      const params = { limit: 1000, page: 1, order: 'id' };
      Object.keys(data).forEach(key => {
        params[key] = data[key];
      });
      const queryString = Object.keys(params || {})
        .map(key => key + '=' + params[key])
        .join('&');

      const response = await axios.get<any>(
        `${process.env.REACT_APP_API_URL}/routers/product-names${queryString ? '?' + queryString : ''}`
      );
      return response.data?.items || [];
    } catch (error) {
      return rejectWithApiError({ thunkAPI, error, fallback: 'Error during getting full product names list!' });
    }
  }
);

export const getFullAuditEntityTypes = createAsyncThunk<any, any, { state: RootReducerState }>(
  'shared/getFullAuditEntityTypes',
  async (data = {}, thunkAPI) => {
    try {
      const params = { limit: 1000, page: 1, order: 'id' };
      Object.keys(data).forEach(key => {
        params[key] = data[key];
      });
      const queryString = Object.keys(params || {})
        .map(key => key + '=' + params[key])
        .join('&');

      const response = await axios.get<any>(
        `${process.env.REACT_APP_API_URL}/tools/audit-logs/entities${queryString ? '?' + queryString : ''}`
      );
      return response.data || [];
    } catch (error) {
      return rejectWithApiError({ thunkAPI, error, fallback: 'Error during getting full entity types list!' });
    }
  }
);

export const getFullExternalSystemList = createAsyncThunk<
  any,
  {
    type: 'TELCO' | 'PBX';
    organizationID: number | null;
    telcoID?: number;
    systemID?: number;
  },
  { state: RootReducerState }
>('shared/getFullExternalSystemList', async ({ type, telcoID, organizationID, systemID }, thunkAPI) => {
  try {
    const typeParam = `type=${type}`;
    const orgParam = organizationID ? `&org_id=${organizationID}` : '';
    const telcoParam = telcoID ? `&telco_id=${telcoID}` : '';
    const systemParam = systemID ? `&system_id=${systemID}` : '';
    const url = `${process.env.REACT_APP_API_URL}/external-systems?${typeParam}${orgParam}${telcoParam}${systemParam}`;

    const response = await axios.get<any>(url);

    if (type === 'PBX') thunkAPI.dispatch(setFullExternalSystemList(response.data || []));
    if (type === 'TELCO') thunkAPI.dispatch(setFullExternalProviderList(response.data || []));

    return response.data || [];
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during getting full external systems list!' });
  }
});

export const getFullProfileList = createAsyncThunk<any, any, { state: RootReducerState }>(
  'shared/getFullProfileList',
  async (data = {}, thunkAPI) => {
    try {
      const params = { limit: 1000, page: 1, order: 'id' };
      Object.keys(data).forEach(key => {
        params[key] = data[key];
      });
      const queryString = Object.keys(params || {})
        .map(key => key + '=' + params[key])
        .join('&');

      const response = await axios.get<any>(
        `${process.env.REACT_APP_API_URL}/atas/profiles/${queryString ? '?' + queryString : ''}`
      );
      return response.data || [];
    } catch (error) {
      return rejectWithApiError({ thunkAPI, error, fallback: 'Error during getting full profiles list!' });
    }
  }
);
